import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Img } from "../common/Img";
import { useLanguage } from "../../context/LanguageContext";

// Decorative floating petal
function Petal({ progress, from, to, x, delay, size, rotate, reduce }) {
  const y = useTransform(progress, [0, 1], reduce ? [0, 0] : [from, to]);
  const opacity = useTransform(progress, [0, 0.2, 0.8, 1], [0, 0.9, 0.9, 0]);
  const rot = useTransform(progress, [0, 1], [0, reduce ? 0 : rotate]);
  return (
    <motion.span
      style={{ y, opacity, rotate: rot, left: x, width: size, height: size * 1.3 }}
      className="pointer-events-none absolute top-1/2 rounded-[50%_50%_50%_50%/60%_60%_40%_40%] bg-[#D9A9AE]/70 blur-[1px]"
      aria-hidden="true"
    />
  );
}

export default function RoseExperience() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  const rotate = useTransform(scrollYProgress, [0, 1], [reduce ? 0 : -14, reduce ? 0 : 12]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.7, 1, 1.12]);
  const roseY = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
  const roseOpacity = useTransform(scrollYProgress, [0, 0.15], [0, 1]);

  const features = t("rose.features");
  const positions = [
    "top-[30%] md:top-[12%] left-[4%] md:left-[8%] text-left",
    "top-[30%] md:top-[12%] right-[4%] md:right-[8%] text-right md:text-left",
    "bottom-[8%] md:bottom-[12%] left-[4%] md:left-[8%] text-left",
    "bottom-[8%] md:bottom-[12%] right-[4%] md:right-[8%] text-right md:text-left",
  ];
  // Each feature reveals across a scroll slice
  const ranges = [
    [0.15, 0.3],
    [0.3, 0.45],
    [0.5, 0.65],
    [0.65, 0.8],
  ];

  return (
    <section
      id="rose"
      ref={ref}
      className="relative bg-[#F6F2EC] h-[280vh] md:h-[320vh]"
      data-testid="rose-section"
    >
      <div className="sticky top-0 h-[100svh] overflow-hidden">
        {/* soft radial lighting backdrop */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="h-[72vmin] w-[72vmin] rounded-full opacity-70"
            style={{
              background:
                "radial-gradient(circle, rgba(196,164,107,0.16) 0%, rgba(246,242,236,0) 68%)",
            }}
          />
        </div>

        {/* Side title */}
        <div className="absolute z-20 top-24 md:top-1/2 md:-translate-y-1/2 left-6 md:left-16 max-w-xs">
          <span className="block font-sans text-[11px] uppercase tracking-[0.24em] text-[#D92A47] mb-4">
            {t("rose.aside")}
          </span>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#183C32]">
            {t("rose.title1")}<br />{t("rose.title2")}
          </h2>
        </div>

        {/* Floating petals */}
        <div className="absolute inset-0 z-0 hidden sm:block">
          {[
            { from: 120, to: -260, x: "38%", size: 26, rotate: 220 },
            { from: 200, to: -360, x: "58%", size: 18, rotate: -180 },
            { from: 80, to: -300, x: "48%", size: 32, rotate: 160 },
            { from: 260, to: -220, x: "64%", size: 14, rotate: 260 },
            { from: 160, to: -320, x: "34%", size: 20, rotate: -200 },
          ].map((p, i) => (
            <Petal key={i} progress={scrollYProgress} reduce={reduce} {...p} />
          ))}
        </div>

        {/* Rose — full bloom, top view, clean cut-out */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <motion.div
            style={{ rotate, scale, y: roseY, opacity: roseOpacity }}
            className="relative h-[64vmin] w-[64vmin] max-h-[560px] max-w-[560px]"
          >
            <Img
              src="/rose-cutout.png"
              alt="A single premium Ecuadorian red rose viewed from above, petals fully open"
              className="h-full w-full object-contain"
              style={{ filter: "drop-shadow(0 34px 55px rgba(217,42,71,0.28))" }}
            />
          </motion.div>
        </div>

        {/* Feature callouts */}
        {features.map((f, i) => {
          const [a, b] = ranges[i];
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const opacity = useTransform(scrollYProgress, [a - 0.05, a, b, b + 0.12], [0, 1, 1, 0.85]);
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const ty = useTransform(scrollYProgress, [a - 0.05, a], [24, 0]);
          return (
            <motion.div
              key={i}
              style={{ opacity, y: ty }}
              className={`absolute z-20 max-w-[150px] md:max-w-[230px] break-words ${positions[i]}`}
              data-testid={`rose-feature-${i}`}
            >
              <div className="font-serif text-base md:text-2xl text-[#D92A47] mb-1.5">{f.title}</div>
              <p className="text-[12px] md:text-sm text-[#817B74] leading-snug">{f.text}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
